


























































import { defineComponent } from '@vue/composition-api';
import ERPRepository from '@/repositories/ERPRepository';
import AccountsLogRepository from '@/repositories/AccountsLogRepository';
import DialogHelper from '@/helpers/DialogHelper';
import PredictedFlowHelper from '@/views/cashFlow/predictedFlow/helpers/PredictedFlowHelper';
import { VForm } from '@/types/VForm';
import PredictedFlowActionsEnum from '@/views/cashFlow/predictedFlow/enums/PredictedFlowActionsEnum';
import ERPEventsEnum from '@/domain/enums/ERPEventsEnum';
import CashFlowTablesEnum from '@/views/cashFlowGroupers/mainGrouper/enums/CashFlowTablesEnum';
import ActionsWithoutReloadEnum from '@/views/cashFlow/predictedFlow/enums/ActionsWithoutReloadEnum';
import AccountActionsEnum from '@/repositories/parameters/AccountLogs/enums/AccountActionsEnum';
import IPredictedFlowGrouperList from '@/views/cashFlow/predictedFlow/interfaces/groupers/IPredictedFlowGrouperList';
import IPredictedFlowGrouperItemData from '@/views/cashFlow/predictedFlow/interfaces/groupers/IPredictedFlowGrouperItemData';
import IChangeDateExternalERPRequestParams from '@/views/cashFlow/predictedFlow/interfaces/actions/IChangeDateExternalERPRequestParams';

export default defineComponent({
  name: 'ChangeDateDialog',
  props: {
    value: {
      required: true,
      type: Boolean,
    },
    companyGroupId: {
      required: true,
      type: Number,
    },
    register: {
      required: false,
      type: Object as () => IPredictedFlowGrouperList,
    },
    childrens: {
      required: false,
      type: Array as () => IPredictedFlowGrouperList[],
    },
    itemIndex: {
      required: false,
      type: Number,
    },
    totalIndex: {
      required: false,
      type: Number,
    },
  },
  computed: {
    dialogWidth(): string {
      return DialogHelper.getDialogWidth(this.$vuetify.breakpoint, {
        smAndDown: '100%',
        md: '60%',
        default: '40%',
      });
    },
    tableName(): string {
      return PredictedFlowHelper.getTableBasedOnNcc(
        this.register!,
        Boolean(this.$session.get('use_ncc')),
      );
    },
    itemData(): IPredictedFlowGrouperItemData {
      return this.register!.itemData!;
    },
    grouperType(): CashFlowTablesEnum {
      return this.register!.grouperType;
    },
  },
  watch: {
    value(): void {
      this.isOpen = this.value;
    },
  },
  data() {
    const erpRepository: ERPRepository = new ERPRepository();
    const accountsLogRepository: AccountsLogRepository = new AccountsLogRepository();

    const date: string = '';

    const loading: boolean = false;
    const isOpen: boolean = this.value;

    return {
      erpRepository,
      accountsLogRepository,
      date,
      loading,
      isOpen,
    };
  },
  methods: {
    handleUpdate(): void {
      if (this.childrens?.length) {
        this.changeDateInBatch();
        return;
      }

      this.changeDate();
    },
    async changeDateInBatch(): Promise<void> {
      try {
        this.loading = true;

        await Promise.all(
          this.childrens!.flatMap((child) => [
            this.updateExternalERP(child),
            this.updateInternalCustomerERP(child),
            this.upsertInternalErp(child),
          ]),
        );
        this.$notification.success('Datas alteradas com sucesso!');

        this.$store.commit('predicted_flow/triggerEventSend', {
          action: ActionsWithoutReloadEnum.change_date,
          itemIndex: this.itemIndex,
          data: {
            value: this.date,
            totalIndex: this.totalIndex,
          },
          isBatch: true,
        });

        this.close();
      } catch (err) {
        this.$notification.error('Houve um problema ao alterar as datas.');
      } finally {
        this.loading = false;
      }
    },
    async changeDate(): Promise<void> {
      try {
        this.loading = true;

        const response = await Promise.allSettled([
          this.updateExternalERP(this.register!),
          this.updateInternalCustomerERP(this.register!),
          this.upsertInternalErp(this.register!),
        ]);

        if (!response.every(({ status }) => status === 'fulfilled')) {
          throw new Error();
        }

        await this.accountsLogRepository.createLog(this.companyGroupId, {
          type:
            this.tableName === 'account_receivable' ? 'receivable' : 'payable',
          company_id: this.itemData.companyId,
          action: AccountActionsEnum.CHANGE_DATE,
          content: 'Data atualizada com sucesso.',
          id_customer: this.itemData.idCustomer,
        });

        this.$notification.success('Data alterada com sucesso!');

        this.$store.commit('predicted_flow/triggerEventSend', {
          action: ActionsWithoutReloadEnum.change_date,
          itemIndex: this.itemIndex,
          data: {
            value: this.date,
            totalIndex: this.totalIndex,
          },
          isBatch: false,
        });

        this.close();
      } catch (error) {
        this.$notification.error('Houve um problema ao alterar a data.');
      } finally {
        this.loading = false;
      }
    },
    async updateExternalERP(data: IPredictedFlowGrouperList): Promise<void> {
      await this.erpRepository.updateExternalERP<IChangeDateExternalERPRequestParams>(
        this.companyGroupId,
        data.itemData!.companyId,
        {
          id: data.itemData!.idCustomer,
          event: ERPEventsEnum.CASH_FLOW_DATE,
          data: {
            cash_flow: this.date.replaceAll('-', ''),
            type: PredictedFlowHelper.getTableBasedOnNcc(
              data!,
              Boolean(this.$session.get('use_ncc')),
            )!,
          },
        },
      );
    },
    async updateInternalCustomerERP(
      data: IPredictedFlowGrouperList,
    ): Promise<void> {
      const fieldBasedOnTable = data.grouperType === CashFlowTablesEnum.ACCOUNTS_RECEIVABLE
          ? 'e1_zdtflux'
          : 'e2_zdtflux';

      await this.erpRepository.updateInternalERP({
        id: data.itemData!.id,
        field: fieldBasedOnTable,
        identify_values: PredictedFlowHelper.getTableAliasByGrouperType(
          data.grouperType,
        ),
        value: this.date,
      });
    },
    async upsertInternalErp(data: IPredictedFlowGrouperList): Promise<void> {
      await this.erpRepository.saveDataInternalTable({
        _identifyValue: PredictedFlowHelper.getTableAliasByGrouperType(
          data.grouperType,
        ),
        _field: 'data',
        _date: data.itemData!.date,
        _keyId: data.itemData!.keyId,
        _data: this.date,
      });
    },
    close(): void {
      this.$emit('close', PredictedFlowActionsEnum.change_date);
    },
    handleChangeDate(): void {
      const changeDateForm = this.$refs.ChangeDateForm as VForm;

      if (!changeDateForm.validate()) {
        this.$notification.error('Há campos preenchidos incorretamente!');
        return;
      }

      this.changeDate();
    },
  },
});
